import { create } from 'zustand';
import helpers from '../../js/helpers';
import Config, { ENV } from '../../js/config/_global';
import Server from '../../js/config/_server';
import { apiGet } from '../../js/api';
import { DeviceType } from '@org-avp/device-models';
import { Stage, AwsSecretSchema } from '@org-avp/models';
import type { AwsSecret } from '@org-avp/models';
import {
  generateShowMediaItemRequest,
  generatePauseMediaItemRequest,
  generateResumeMediaItemRequest,
  generateStopMediaItemRequest,
  generateGetStatesRequest,
  generateStartSessionRequest,
  generateEndSessionRequest,
  AveMessageType,
} from '@org-avp/ave-message-factory';
import type {
  AveRequest,
  AveResponse,
  AveError,
} from '@org-avp/ave-message-factory';
import { WebrtcPlayer } from '@org-avp/webrtc-player';
import { Logger, createBrowserConsoleTransport } from '@org-avp/logger';
import { COOKIE_KEYS, ENV_TYPES } from '../../js/_constants';
import { getMiwiDevices, setAssetChrombox, controlMediaChrombox } from '@org-avp/2d-message-factory';
import { getContextToken, getNerdDevices, startSessionNeRD, endSessionNeRD, controlMediaNeRD, setAssetNeRD } from '@org-avp/2d-message-factory';

export const STREAMING_CONTAINER_ID = 'ave-stream';
export let SessionIdTimeout: ReturnType<typeof setTimeout> | null = null;

const appStageToAveStageMap = {
  [ENV_TYPES.DEV]: Stage.DEVELOP,
  [ENV_TYPES.INT]: Stage.PREPROD,
  [ENV_TYPES.PROD]: Stage.PROD,
};

const logger = new Logger({
  application: {
    app: 'RCM',
    version: Config.version,
    stage: appStageToAveStageMap[ENV] as Stage,
  },
  initiator: 'avpdevdfe',
  transports: [createBrowserConsoleTransport()],
  logLevel: 'debug',
});

const getLogger = () => logger;

const log = getLogger();

export const getAppStage = (): Stage => {
  if (ENV === ENV_TYPES.DEV) {
    return Stage.DEVELOP;
  }
  if (ENV === ENV_TYPES.INT) {
    return Stage.PREPROD;
  }
  return Stage.PROD;
};

export const fetchCredentialsFromTvm = async (
  overrideDealerId: string
): Promise<AwsSecret> => {
  const url =
    Server.baseUrl + Server.services.miwiAuthentication + overrideDealerId;
  const response = await apiGet(url);
  const credentials = AwsSecretSchema.safeParse(response);
  if (!credentials.success) {
    throw new Error('Malformed backend data', {
      cause: { error: credentials.error, response },
    });
  }
  return credentials.data;
};

const _handleAveMessage = (msg: AveResponse<unknown>[]) => {
  const messageIsError = (message: AveResponse<unknown>): message is AveError =>
    message.meta.type === AveMessageType.ERROR;
  log.debug(`Received a message from the AVE - ${msg?.[0].meta.name}`, {
    msg: msg.length === 1 ? msg[0] : msg,
  });
  msg.filter(messageIsError).forEach(message => {
    log.warn('Received an AVE error', { error: message });
  });
};

const _handleDisconnect = () => {
  log.debug('WebrtcPlayer was disconnected from AVE');
};

const player = new WebrtcPlayer(log);

player.on('disconnected', _handleDisconnect);
player.on('aveMessage', _handleAveMessage);

const sendMessage = (request: AveRequest<unknown>): void => {
  if (!player.isConnected()) {
    throw new Error('Not connected');
  }
  player.sendAveRequest({ ...request });
};

type MediaControlCommand = 'SKIP' | 'PLAY' | 'PAUSE' | 'STOP';
type MediaControl3DCommand = 'STOP' | 'PLAY' | 'PAUSE';
type ChromeboxMediaType = 'image' | 'video';
type ChromeboxControlCommand = 'SKIP' | 'PLAY' | 'PAUSE' | 'STOP';

interface MiwiStoreState {
  overrideDeviceId: string;
  overrideDealerId: string;
  overrideDevices: any[];
  overrideMediaUrl: string;
  overrideMedia: any;
  overrideMediaType: string;
  overrideLoading: boolean;
  overrideSessionId: string;
  overrideDeviceType: string;
  appENV: Stage;
  setOverrideDealerId: (overrideDealerId: string) => void;
  setOverrideLoading: (overrideLoading: boolean) => void;
  setOverrideDeviceId: (overrideDeviceId: string) => void;
  setOverrideSessionId: (overrideSessionId: string) => void;
  setOverrideMediaUrl: (overrideMediaUrl: string) => void;
  setOverrideMedia: (overrideMedia: string) => void;
  setOverrideDeviceType: (overrideDeviceType: string) => void;
  fetchNaapDevices: () => Promise<void>;
  setOverride: (media: string, mediaType: string, mediaUrl: string) => Promise<void>;
  controlMedia: (command: MediaControlCommand) => Promise<void>;
  connectDevice: (overrideDeviceId: string) => Promise<void>;
  disconnectDevice: () => Promise<void>;
  overrideLogOut: () => Promise<void>;
  fetchMiwiDevices: () => Promise<void>;
  setAsset2D: (mediaType: ChromeboxMediaType, mediaUrl: string, media: string) => Promise<void>;
  setAsset3D: (type: string, url: string, media: string) => Promise<void>;
  controlMedia2D: (command: ChromeboxControlCommand) => Promise<void>;
  controlMedia3D: (command: MediaControl3DCommand) => Promise<void>;
  connectDeviceChromebox: (overrideDeviceId: string) => Promise<void>;
  connectPrivateAve: (id: string) => Promise<void>;
  connectNerdDevice: (overrideDeviceId: string) => Promise<void>;
  disconnect: () => Promise<void>;
  miwiLogout: () => Promise<void>;
  setContextToken: () => Promise<void>;
}

const useMiwiStore = create<MiwiStoreState>((set, get) => ({
  overrideDeviceId: '',
  overrideDealerId: helpers.getOverrideDealerId() || '',
  overrideDevices: [],
  overrideMediaUrl: '',
  overrideMedia: '',
  overrideMediaType: '',
  overrideLoading: false,
  overrideSessionId: '',
  overrideDeviceType: helpers.getOverrideDeviceType() || '',
  appENV: getAppStage(),

  setOverrideDealerId: overrideDealerId => set({ overrideDealerId }),
  setOverrideLoading: overrideLoading => set({ overrideLoading }),
  setOverrideDeviceId: overrideDeviceId => set({ overrideDeviceId }),
  setOverrideSessionId: overrideSessionId => set({ overrideSessionId }),
  setOverrideMediaUrl: overrideMediaUrl => set({ overrideMediaUrl }),
  setOverrideMedia: overrideMedia => set({ overrideMedia }),
  setOverrideDeviceType: overrideDeviceType => set({ overrideDeviceType }),

  setContextToken: async () => {
    const state = get();
    const contextToken = await getContextToken(state.appENV, state.overrideDealerId);
    helpers.setCookie(COOKIE_KEYS.CONTEXT_TOKEN, contextToken, 1 / 12)
  },

  fetchNaapDevices: async () => {
    const state = get();
    set({ overrideLoading: true });
    await state.setContextToken();
    const overrideDevices = await getNerdDevices(state.appENV, helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN));
    set({ overrideDevices, overrideLoading: false });
  },

  setOverride: async (media, mediaType, mediaUrl) => {
    const state = get();
    !helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN) && await state.setContextToken();
    await setAssetNeRD(
      state.appENV,
      state.overrideDeviceId,
      state.overrideSessionId,
      helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN),
      mediaType as any,
      mediaUrl
    );
    set({ overrideMediaUrl: mediaUrl, overrideMedia: media });
  },

  controlMedia: async command => {
    const state = get();
    !helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN) && await state.setContextToken();
    if (command === 'SKIP') {
      await controlMediaNeRD(
        state.appENV,
        state.overrideDeviceId,
        state.overrideSessionId,
        helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN),
        'skip');
      set({ overrideMediaUrl: '', overrideMedia: '' });
      return;
    }
    if (command === 'PLAY') {
      await controlMediaNeRD(
        state.appENV,
        state.overrideDeviceId,
        state.overrideSessionId,
        helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN),
        'play'
      );
    }
    if (command === 'PAUSE') {
      await controlMediaNeRD(
        state.appENV,
        state.overrideDeviceId,
        state.overrideSessionId,
        helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN),
        'pause'
      );
    }
  },

  connectDevice: async overrideDeviceId => {
    const state = get();
    set({ overrideLoading: true });
    !helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN) && await state.setContextToken();
    if (state.overrideSessionId) {
      await endSessionNeRD(
        state.appENV,
        state.overrideDeviceId,
        state.overrideSessionId,
        helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN)
      );
    }
    const sessionId = await startSessionNeRD(state.appENV,
      overrideDeviceId,
      helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN)
    );
    localStorage.setItem('overrideDeviceId', overrideDeviceId);
    await state.fetchNaapDevices();
    set({
      overrideDeviceType: 'NeRD',
      overrideDeviceId,
      overrideSessionId: sessionId,
      overrideLoading: false,
      overrideMediaUrl: '',
      overrideMedia: '',
    });
  },

  disconnectDevice: async () => {
    const state = get();
    !helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN) && await state.setContextToken();
    await endSessionNeRD(
      state.appENV,
      state.overrideDeviceId,
      state.overrideSessionId,
      helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN)
    );
    await state.fetchNaapDevices();
    localStorage.setItem('overrideDeviceId', '');
    set({
      overrideDeviceId: '',
      overrideMediaUrl: '',
      overrideMedia: '',
      overrideSessionId: '',
    });
  },

  overrideLogOut: async () => {
    const state = get();
    !helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN) && await state.setContextToken();
    await endSessionNeRD(
      state.appENV,
      state.overrideDeviceId,
      state.overrideSessionId,
      helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN)
    );
    localStorage.removeItem('overrideDeviceId');
    localStorage.removeItem('overrideDealerId');
    set({
      overrideDeviceId: '',
      overrideDealerId: '',
      overrideMediaUrl: '',
      overrideMedia: '',
      overrideDevices: [],
      overrideSessionId: '',
    });
  },

  fetchMiwiDevices: async () => {
    const state = get();
    set({ overrideLoading: true });
    const overrideDevices = await getMiwiDevices(state.appENV,
      state.overrideDealerId,
      helpers.getCookie(COOKIE_KEYS.TOKEN)
    );
    set({ overrideDevices, overrideLoading: false });
  },

  setAsset2D: async (mediaType, mediaUrl, media) => {
    const state = get();
    set({ overrideLoading: true });

    await setAssetChrombox(
      state.appENV,
      state.overrideDeviceId,
      state.overrideDealerId,
      helpers.getCookie(COOKIE_KEYS.TOKEN),
      mediaType,
      mediaUrl
    );

    set({
      overrideLoading: false,
      overrideMediaUrl: mediaUrl,
      overrideMediaType: mediaType,
      overrideMedia: media,
    });
  },

  setAsset3D: async (type, url, media) => {
    set({ overrideLoading: true });
    sendMessage(generateShowMediaItemRequest({ type: type as any, url}));
    set({
      overrideLoading: false,
      overrideMediaUrl: url,
      overrideMediaType: type,
      overrideMedia: media,
    });
  },

  controlMedia2D: async command => {
    const state = get();
    set({ overrideLoading: true });

    await controlMediaChrombox(
      state.appENV,
      state.overrideDeviceId,
      state.overrideDealerId,
      helpers.getCookie(COOKIE_KEYS.TOKEN),
      command
    );

    if (command === 'SKIP') {
      set({ overrideLoading: false, overrideMediaUrl: '', overrideMedia: '' });
      return;
    }

    set({ overrideLoading: false });
  },

  controlMedia3D: async command => {
    set({ overrideLoading: true });

    if (command === 'STOP') {
      const message = generateStopMediaItemRequest();
      await player.sendAveRequest(message);
      set({ overrideMediaUrl: '', overrideMedia: '', overrideLoading: false });
      return;
    }

    if (command === 'PLAY') {
      const message = generateResumeMediaItemRequest();
      await player.sendAveRequest(message);
      set({ overrideLoading: false });
      return;
    }

    if (command === 'PAUSE') {
      const message = generatePauseMediaItemRequest();
      await player.sendAveRequest(message);
      set({ overrideLoading: false });
    }
  },

  connectDeviceChromebox: async overrideDeviceId => {
    const state = get();
    SessionIdTimeout && clearTimeout(SessionIdTimeout);
    set({ overrideLoading: true });

    if (state.overrideDeviceId) {
      await state.disconnect();
    }

    localStorage.setItem('overrideDeviceId', overrideDeviceId);
    log.debug('Selecting Chromebox', { overrideDeviceId });
    localStorage.setItem('overrideDeviceType', DeviceType.CHROMEBOX);

    set({
      overrideDeviceType: DeviceType.CHROMEBOX,
      overrideDeviceId,
      overrideLoading: false,
      overrideMediaUrl: '',
      overrideMedia: '',
      overrideSessionId: '',
    });
  },

  connectPrivateAve: async (id) => {
    const state = get();

    SessionIdTimeout && clearTimeout(SessionIdTimeout);
    set({ overrideLoading: true });
    localStorage.setItem('overrideDeviceId', id);

    if (state.overrideDeviceId) {
      await state.disconnect();
    }

    log.debug('Connecting to private AVE', { id });

    try {
      await player.connect({
        dataChannelOnly: true,
        htmlContainerId: STREAMING_CONTAINER_ID,
        signalingOptions: {
          type: 'aws',
          credentialsProvider: () => fetchCredentialsFromTvm(state.overrideDealerId),
          channelName: id,
          clientId: `${id}-override`,
        },
      });

      sendMessage(
        generateStartSessionRequest({
          // this is not the webrtc clientId. It is only needed for analytics.
          // See AVE StartSession documentation.
          client: `RCM;${Config.version};${ENV}`,
          sessionId: `${id.split("_")[0]}-${id.split("_")[1]}-override`,
        })
      );

      // Immediately request current state of AVE.
      sendMessage(generateGetStatesRequest());
      localStorage.setItem('overrideDeviceType', DeviceType.AVE);

      set({
        overrideDeviceType: DeviceType.AVE,
        overrideDeviceId: id,
        overrideLoading: false,
        overrideMediaUrl: '',
        overrideMedia: '',
        overrideSessionId: '',
      });
    } catch (error: unknown) {
      log.error('Error connecting to private AVE', { id, error });
      set({
        overrideDeviceId: id,
        overrideLoading: false,
        overrideMediaUrl: '',
        overrideMedia: '',
      });
    }
  },

  connectNerdDevice: async overrideDeviceId => {
    const state = get();

    if (state.overrideDeviceId) {
      await state.disconnect();
    }

    set({ overrideLoading: true });
    !helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN) && await state.setContextToken();
    const sessionId = await startSessionNeRD(state.appENV,
      overrideDeviceId,
      helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN)
    );

    if (!sessionId) {
      set({ overrideLoading: false });
      return;
    }

    localStorage.setItem('overrideDeviceId', overrideDeviceId);
    localStorage.setItem('overrideDeviceType', 'NeRD');

    SessionIdTimeout && clearTimeout(SessionIdTimeout);
    SessionIdTimeout = setTimeout(() => {
      localStorage.setItem('overrideDeviceId', '');
      state.setOverrideDeviceId('');
      state.setOverrideSessionId('');
      state.setOverrideMediaUrl('');
      state.setOverrideMedia('');
    }, 5 * 60 * 1000);

    await state.fetchMiwiDevices();

    set({
      overrideSessionId: sessionId,
      overrideDeviceType: 'NeRD',
      overrideDeviceId,
      overrideLoading: false,
      overrideMediaUrl: '',
      overrideMedia: '',
    });
  },

  disconnect: async () => {
    const state = get();
    set({ overrideLoading: true });

    if (state.overrideDeviceType === DeviceType.CHROMEBOX) {
      await controlMediaChrombox(
        state.appENV,
        state.overrideDeviceId,
        state.overrideDealerId,
        helpers.getCookie(COOKIE_KEYS.TOKEN),
        'SKIP'
      );
    }

    if (state.overrideDeviceType === DeviceType.AVE) {
      log.debug('Disconnecting from AVE');
      if (player.isConnected()) {
        log.debug('Sending EndSession request to private AVE');
        sendMessage(generateEndSessionRequest());
      }
      await player.disconnect();
    }

    if (state.overrideDeviceType === 'NeRD' && state.overrideSessionId) {
      !helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN) && await state.setContextToken();
      await endSessionNeRD(
        state.appENV,
        state.overrideDeviceId,
        state.overrideSessionId,
        helpers.getCookie(COOKIE_KEYS.CONTEXT_TOKEN)
      );
    }

    localStorage.setItem('overrideDeviceId', '');
    localStorage.setItem('overrideDeviceType', '');

    set({
      overrideLoading: false,
      overrideDeviceId: '',
      overrideDeviceType: '',
      overrideMediaUrl: '',
      overrideMedia: '',
      overrideSessionId: '',
    });
  },

  miwiLogout: async () => {
    const state = get();
    state.overrideDeviceId && await state.disconnect();
    localStorage.removeItem('overrideDeviceType');
    localStorage.removeItem('overrideDeviceId');
    localStorage.removeItem('overrideDealerId');

    set({
      overrideDeviceType: '',
      overrideDeviceId: '',
      overrideDealerId: '',
      overrideMediaUrl: '',
      overrideMedia: '',
      overrideDevices: [],
    });
  },
}));

export default useMiwiStore;
